import {
    Briefcase,
    Building2,
    CalendarDays,
    ChevronLeft,
    Clock,
    IdCard,
    Loader2,
    Mail,
    MapPin,
    MinusCircle,
    Phone,
    PiggyBank,
    User,
    UserCog,
    Wallet,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { useLocation, useNavigate } from "react-router-dom";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { useEffect, useMemo, useState } from "react";
import { SUBORDINATE_ATTENDANCE_SUMMARY_URL, SUBORDINATES_URL } from "../../constants/api";
import { Reactable } from "../../components/ui/reactable";
import dayjs from "dayjs";
import EmployeeAllowanceContent from "./helpers/EmployeeAllowanceContent";
import EmployeeDeductionContent from "./helpers/EmployeeDeductionContent";
import EmployeeDepositContent from "./helpers/EmployeeDepositContent";
import { cn } from "../../utils/tailwindUtils";
import { formatEmployeeDisplayName } from "../../utils/employeeDisplayName";
import male from "../../assets/svg/Male.svg";

const show = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const Field = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2.5">
        {Icon && <Icon className="w-4 h-4 mt-0.5 shrink-0 text-sidebar-primary/70" />}
        <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide font-medium text-gray-400 dark:text-gray-500">{label}</p>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 break-words">{show(value)}</p>
        </div>
    </div>
);

const SectionCard = ({ icon: Icon, title, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 flex items-center gap-2 mb-4">
            {Icon && <Icon className="w-4 h-4 text-sidebar-primary" />} {title}
        </h3>
        {children}
    </div>
);

const SubordinateViewDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { employeeID, subordinateId } = location?.state || {};

    const { fetch } = useApiMethods();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [attendanceSummary, setAttendanceSummary] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [tab, setTab] = useState("overview");

    const getSubordinateDetailsById = async (id) => {
        if (!id) return;
        try {
            setLoading(true);
            const response = await fetch(SUBORDINATES_URL, { subordinate_id: id });

            if (response.status === "success") {
                setData(response.data[0] || null);
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getSubordinateDetailsById(subordinateId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subordinateId]);

    const displayName = useMemo(
        () => formatEmployeeDisplayName({ employee_name: data?.employee_name }),
        [data?.employee_name],
    );

    const tabs = useMemo(
        () => [
            { key: "overview", label: "Overview", icon: User },
            { key: "allowances", label: "Allowances", icon: Wallet },
            { key: "deductions", label: "Deductions", icon: MinusCircle },
            { key: "deposits", label: "Deposits", icon: PiggyBank },
            { key: "attendance", label: "Attendance", icon: CalendarDays },
        ],
        [],
    );

    const dayWiseAttendanceColumns = [
        {
            accessor: "date",
            Header: "Date",
            filterable: true,
            Cell: ({ value }) => value ?? "—",
        },
        {
            accessor: "shift",
            Header: "Shift",
            filterable: true,
            Cell: ({ value }) => (
                <span className="inline-flex max-w-[180px] truncate rounded-md bg-gray-100 dark:bg-gray-700/80 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-200">
                    {value ?? "—"}
                </span>
            ),
        },
        {
            accessor: "check_in",
            Header: "Check In",
            Cell: ({ value }) =>
                value && dayjs(value).isValid() ? dayjs(value).format("hh:mm A") : "—",
        },
        {
            accessor: "check_out",
            Header: "Check Out",
            Cell: ({ value }) =>
                value && dayjs(value).isValid() ? dayjs(value).format("hh:mm A") : "—",
        },
        {
            accessor: "overtime",
            Header: "Overtime",
            Cell: ({ value }) => value ?? "—",
        },
        {
            accessor: "break_hrs",
            Header: "Break Hours",
            Cell: ({ value }) => value ?? "—",
        },
        {
            accessor: "total_working_hours",
            Header: "Total Hours",
            Cell: ({ value }) => (
                <span className="font-medium tabular-nums text-sidebar-primary">{value ?? "—"}</span>
            ),
        },
        {
            accessor: "location",
            Header: "Location",
            Cell: ({ value }) => value ?? "—",
        },
    ];

    const avatarSrc = data?.profile || male;

    return (
        <ContainerCard scrollable>
            <div className="px-3 pt-1 pb-2">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-sidebar-primary transition-colors mb-3"
                >
                    <ChevronLeft className="w-4 h-4" /> Back to subordinates
                </button>

                {loading ? (
                    <div className="flex items-center justify-center py-24">
                        <Loader2 className="w-7 h-7 animate-spin text-sidebar-primary" />
                    </div>
                ) : !data ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                        Could not load subordinate details.
                    </p>
                ) : (
                    <div className="space-y-3 pb-6">
                        <div className="relative overflow-hidden rounded-2xl border border-sidebar-primary/20 shadow-sm bg-gradient-to-br from-sidebar-primary to-teal-600 text-white p-5">
                            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
                            <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
                                <img
                                    src={avatarSrc}
                                    alt={displayName}
                                    onError={(ev) => {
                                        ev.currentTarget.src = male;
                                    }}
                                    className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white/30 bg-white shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                    <h1 className="text-2xl font-bold leading-tight truncate" title={displayName}>
                                        {displayName}
                                    </h1>
                                    <p className="text-white/85 text-sm mt-0.5 truncate">
                                        {show(data.designation)}
                                        {data.department ? ` · ${data.department}` : ""}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-white/80">
                                        <span className="inline-flex items-center gap-1">
                                            <IdCard className="w-3.5 h-3.5" /> {show(data.employee_id)}
                                        </span>
                                        {data.employee_type && (
                                            <span className="inline-flex items-center gap-1">
                                                <Briefcase className="w-3.5 h-3.5" /> {data.employee_type}
                                            </span>
                                        )}
                                        {data.shift && (
                                            <span className="inline-flex items-center gap-1">
                                                <Clock className="w-3.5 h-3.5" /> {data.shift}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1 text-sm text-white/90 sm:text-right sm:min-w-[200px]">
                                    {data.email && (
                                        <p className="inline-flex sm:justify-end items-center gap-1.5 truncate">
                                            <Mail className="w-3.5 h-3.5 shrink-0" /> {data.email}
                                        </p>
                                    )}
                                    {data.phone_number && (
                                        <p className="inline-flex sm:justify-end items-center gap-1.5">
                                            <Phone className="w-3.5 h-3.5 shrink-0" /> {data.phone_number}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-1 overflow-x-auto bg-gray-100 dark:bg-gray-900/70 rounded-xl p-1">
                            {tabs.map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => setTab(t.key)}
                                    className={cn(
                                        "inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap transition-all",
                                        tab === t.key
                                            ? "bg-background text-sidebar-primary shadow-sm"
                                            : "text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200",
                                    )}
                                >
                                    <t.icon className="w-4 h-4" /> {t.label}
                                </button>
                            ))}
                        </div>

                        {tab === "overview" && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                <SectionCard icon={Briefcase} title="Work">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <Field icon={Briefcase} label="Designation" value={data.designation} />
                                        <Field icon={Building2} label="Department" value={data.department} />
                                        <Field icon={UserCog} label="Reporting Manager" value={data.reporting_manager} />
                                        <Field icon={Clock} label="Standing shift" value={data.shift} />
                                        <Field icon={MapPin} label="Work location" value={data.work_location} />
                                        <Field icon={IdCard} label="Employee ID" value={data.employee_id} />
                                    </div>
                                </SectionCard>
                                <SectionCard icon={Mail} title="Contact">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <Field icon={Mail} label="Email" value={data.email} />
                                        <Field icon={Phone} label="Phone" value={data.phone_number} />
                                        <Field icon={MapPin} label="City / location" value={data.location} />
                                        <Field icon={Briefcase} label="Employee type" value={data.employee_type} />
                                    </div>
                                </SectionCard>
                            </div>
                        )}

                        {tab === "allowances" && subordinateId && (
                            <EmployeeAllowanceContent employeeID={subordinateId} />
                        )}

                        {tab === "deductions" && subordinateId && (
                            <EmployeeDeductionContent employeeID={subordinateId} />
                        )}

                        {tab === "deposits" && subordinateId && (
                            <EmployeeDepositContent employeeID={subordinateId} />
                        )}

                        {tab === "attendance" && subordinateId && employeeID && (
                            <SectionCard icon={CalendarDays} title="Day-wise attendance">
                                <Reactable
                                    fillHeight={false}
                                    columns={dayWiseAttendanceColumns}
                                    data={attendanceSummary}
                                    setData={setAttendanceSummary}
                                    setTotalCount={setTotalRecords}
                                    url={SUBORDINATE_ATTENDANCE_SUMMARY_URL}
                                    serverSide
                                    rowSize="md"
                                    isCSVRequired
                                    isPDFRequired
                                    isMonthYearRequired
                                    filter={{ employee_id: employeeID }}
                                    printFilter={{ employee_id: employeeID }}
                                    manualPagination
                                />
                                {totalRecords > 0 && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                        {totalRecords} record{totalRecords === 1 ? "" : "s"} in selected period
                                    </p>
                                )}
                            </SectionCard>
                        )}
                    </div>
                )}
            </div>
        </ContainerCard>
    );
};

export default SubordinateViewDetails;
